import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import useToggle from '../../../Hooks/useToggle';
import pageBanner from '../../../assets/images/frontdesk-banner.webp';
import frontdeskImg2 from '../../../assets/images/frontdesk-booking-view.webp';
import frontdeskImg3 from '../../../assets/images/frontdesk-reservations.webp';
import frontdeskImg1 from '../../../assets/images/frontdesk-room-view.webp';
import BackToTop from '../../BackToTop';
import FooterHomeThree from '../../Footer/FooterHomeThree';
import HeaderNews from '../../Header/HeaderNews';
import HeroNews from '../../Header/HeroNews';
import Drawer from '../../Mobile/Drawer';
import PreviewSlider from '../../PreviewSlider';
import ServicesAbout from './ServicesAbout';
import ShowCase from './ShowCase';

// Shown on the page and given to search engines as structured data; keep the two the same.
const FAQ = [
    [
        'What does OTO Front Desk do?',
        'It runs a hotel’s rooms and guests: bookings, check-in and check-out, room status and housekeeping, and each guest’s bill, with reports for the owner.',
    ],
    [
        'Can the restaurant and bar charge drinks and meals to a room?',
        'Yes. With OTO POS, the waiter picks the guest’s room and the charge goes straight onto that guest’s bill, so nothing is copied out by hand at check-out.',
    ],
    [
        'Does it work with Booking.com, Expedia, Airbnb and Agoda?',
        'Yes, through the OTO Channel Manager: bookings from those sites arrive in OTO automatically and your availability stays in sync, so a room isn’t sold twice.',
    ],
    [
        'Is it only for big hotels?',
        'No. It suits guest houses and small hotels as well as larger hotels, and you start with the parts you need.',
    ],
    [
        'How much does it cost?',
        'It depends on the number of rooms and the parts you use. Ask us for a quote and a demo.',
    ],
];

const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ.map(([question, answer]) => ({
        '@type': 'Question',
        name: question,
        acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
};

function Frontdesk() {
    const [drawer, drawerAction] = useToggle(false);
    return (
        <>
            <Helmet>
                <meta
                    name="description"
                    content="Rooms, bookings, check-in, check-out, housekeeping and guest bills in one place. Restaurant and bar charges go straight to the room. Made in Rwanda."
                />
                <title>Hotel Management App & Front Desk Software | OTO by Kapp</title>
                <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
            </Helmet>
            <Drawer drawer={drawer} action={drawerAction.toggle} />
            <HeaderNews action={drawerAction.toggle} />
            <main>
                <HeroNews
                    title="Hotel Management App and Front Desk Software"
                    breadcrumb={[
                        { link: '/', title: 'Home' },
                        { link: '/apps', title: 'Apps' },
                        { link: '/apps/frontdesk', title: 'Frontdesk' },
                    ]}
                />

                <div
                    className="container pt-90 pb-90"
                    style={{ fontSize: '18px', lineHeight: '29px' }}
                >
                    <h2 className="mb-4">Hotel management software for Rwanda</h2>
                    <p className="mb-3">
                        <strong>
                            OTO Front Desk runs your hotel&apos;s rooms, bookings, check-in and
                            check-out, housekeeping and guest bills in one place, and the restaurant
                            and bar can charge straight to a guest&apos;s room.
                        </strong>
                    </p>
                    <p className="mb-3">
                        OTO Frontdesk provides you with an intuitive interface to manage rooms and
                        reservations, making it easy to keep track of bookings, availability, and
                        pricing.
                    </p>
                    <p>
                        Suitable for small bed and breakfasts, small hotels, medium and large
                        hotels. With our app, you can simplify the reservation process, improve
                        efficiency, and provide a better guest experience.
                    </p>
                    <p>
                        Additionally our room management app and POS app are designed to work
                        seamlessly together, providing you with a single system to manage all
                        aspects of your hotel operations
                    </p>
                    <p>
                        Sell on Booking.com, Expedia, Airbnb and Agoda without double bookings with
                        the <Link to="/apps/channel-manager">OTO Channel Manager</Link>.
                    </p>

                    <img
                        loading="lazy"
                        className="page-banner-img mt-4"
                        src={pageBanner}
                        alt="OTO Front Desk room view"
                    />
                </div>
                <ServicesAbout />

                <ShowCase />
                <div className="container">
                    <PreviewSlider
                        images={[
                            { url: frontdeskImg1, alt: 'Frontdesk Rooms List' },
                            { url: frontdeskImg2, alt: 'Frontdesk Booking View' },
                            { url: frontdeskImg3, alt: 'Frontdesk Reservations' },
                        ]}
                    />
                </div>
                <div className="container pt-60 pb-80">
                    <div className="mx-auto" style={{ maxWidth: '800px' }}>
                        <h2 className="mb-4">Questions hotels ask</h2>
                        {FAQ.map(([question, answer]) => (
                            <div key={question} className="mb-4">
                                <h3 className="h5">{question}</h3>
                                <p>{answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </main>
            <FooterHomeThree className="appie-footer-about-area" />
            <BackToTop />
        </>
    );
}

export default Frontdesk;
